// Course / lesson Q&A. Reads are gated by RLS to enrolled students + admins.
import { supabase } from '../integrations/supabase/client';

export interface DiscussionPost {
  id: string;
  course_id: string;
  lesson_id: string | null;
  user_id: string;
  parent_id: string | null;
  body: string;
  created_at: string;
  author_name?: string;
  author_role?: string;
  replies?: DiscussionPost[];
}

async function attachAuthors(rows: DiscussionPost[]): Promise<DiscussionPost[]> {
  const ids = [...new Set(rows.map((r) => r.user_id))];
  if (ids.length === 0) return rows;
  let names: Record<string, { name: string; role: string }> = {};
  try {
    const { data } = await supabase.from('profiles_public').select('id,full_name,role').in('id', ids);
    (data ?? []).forEach((p) => {
      const row = p as unknown as { id: string; full_name: string; role: string };
      names[row.id] = { name: row.full_name || 'Student', role: row.role };
    });
  } catch {
    names = {};
  }
  return rows.map((r) => ({ ...r, author_name: names[r.user_id]?.name || 'Student', author_role: names[r.user_id]?.role || 'STUDENT' }));
}

export async function listCourseDiscussions(courseId: string, lessonId?: string): Promise<DiscussionPost[]> {
  try {
    let query = supabase.from('discussions').select('*').eq('course_id', courseId).order('created_at', { ascending: true }).limit(200);
    if (lessonId) query = query.eq('lesson_id', lessonId);
    const { data, error } = await query;
    if (error) return [];
    const rows = await attachAuthors((data ?? []) as unknown as DiscussionPost[]);
    const byId = new Map(rows.map((r) => [r.id, { ...r, replies: [] as DiscussionPost[] }]));
    const roots: DiscussionPost[] = [];
    byId.forEach((r) => {
      if (r.parent_id && byId.has(r.parent_id)) byId.get(r.parent_id)!.replies!.push(r);
      else roots.push(r);
    });
    return roots;
  } catch {
    return [];
  }
}

export async function postDiscussion(args: {
  userId: string;
  courseId: string;
  lessonId?: string | null;
  parentId?: string | null;
  body: string;
}): Promise<void> {
  const { error } = await supabase.from('discussions').insert({
    user_id: args.userId,
    course_id: args.courseId,
    lesson_id: args.lessonId || null,
    parent_id: args.parentId || null,
    body: args.body.trim(),
  });
  if (error) throw new Error(error.message);
}

export async function deleteDiscussion(id: string): Promise<void> {
  const { error } = await supabase.from('discussions').delete().eq('id', id);
  if (error) throw new Error(error.message);
}
